import React from 'react';
import PropTypes from 'prop-types';
import {fromJS, Map} from 'immutable';
import track from 'react-tracking';
import {pushQueryParams} from 'client/history';
import withTracking from 'lib/hocs/withTracking';
import {callAction} from 'client/framework';
import appStore from 'client/AppStore';
import Tabs from 'sg/Tabs/Tabs.react';
import {MandarkTable, Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import {renderMoment} from 'lib/timeUtils';
import {RANGES} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import AssignmentCompletionStatus from 'client/Classrooms/shared/AssignmentCompletionStatus/AssignmentCompletionStatus.react';
import {addGenericErrorToast, addToast, Button, DatePicker} from '@albert-io/atomic';

import TeacherPracticeExamOptionsDropdown from 'client/Classrooms/shared/TeacherPracticeExamOptionsDropdown/TeacherPracticeExamOptionsDropdown.react';

import teacherPracticeExamsListActions from './TeacherPracticeExamsList.actions';
import teacherPracticeExamsListStore from './TeacherPracticeExamsList.store';
import AssignPracticeExamModal from './AssignPracticeExamModal/AssignPracticeExamModal.react';

import './teacher-practice-exams-list.scss';

const TrackedButton = withTracking(Button, {
  onClick: {
    event: 'click',
    type: 'button',
    component: null,
    content: 'Assign assignment'
  }
});

const TrackedDatePickerSelectField = withTracking(DatePicker.SelectField, {
  onChange: {
    component: 'filter_menu',
    content: (e) => e.target.value,
    event: 'change',
    type: 'dropdown item'
  }
});

const TrackedDatePickerStartDateField = withTracking(DatePicker.StartDateField, {
  onChange: {
    component: 'Date filter',
    content: 'Start',
    event: 'change',
    type: 'input'
  }
});

const TrackedDatePickerEndDateField = withTracking(DatePicker.EndDateField, {
  onChange: {
    component: 'Date filter',
    content: 'End',
    event: 'change',
    type: 'input'
  }
});

const TrackedDatePickerApplyBtn = withTracking(DatePicker.ApplyBtn, {
  onClick: {
    component: 'Date filter',
    content: (e) =>
      `${e.target.dataset.range} ${e.target.dataset.start} ${e.target.dataset.end}${
        e.target.dataset.valid === 'false' ? ' was invalid' : ''
      }`,
    event: 'click',
    type: 'button'
  }
});

@track({component: 'table', feature: 'Assessments List'})
class TeacherPracticeExamsList extends React.Component {
  static propTypes = {
    location: PropTypes.object,
    tracking: PropTypes.object
  };

  constructor() {
    super();

    this.state = {
      showAssignPracticeExamModal: false
    };

    this.tabs = fromJS([
      {
        label: 'All',
        query: {
          examStatus: 'all'
        },
        badgeContent: () => teacherPracticeExamsListStore.getAssignmentCountsByStatus().all
      },
      {
        label: 'Open',
        query: {
          examStatus: null
        },
        badgeContent: () => teacherPracticeExamsListStore.getAssignmentCountsByStatus().open
      },
      {
        label: 'Scheduled',
        query: {
          examStatus: 'scheduled'
        },
        badgeContent: () => teacherPracticeExamsListStore.getAssignmentCountsByStatus().scheduled
      },
      {
        label: 'Closed',
        query: {
          examStatus: 'closed'
        },
        badgeContent: () => teacherPracticeExamsListStore.getAssignmentCountsByStatus().closed
      }
    ]);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    callAction(teacherPracticeExamsListActions.UPDATE_VIEW_LOADED_AT_DATETIME);
  }

  componentDidMount() {
    const {location} = this.props;
    const {start = '', end = ''} = location.query;

    callAction(teacherPracticeExamsListActions.SET_LAST_VALID_DATES, {
      startDate: start,
      endDate: end
    });
  }

  componentDidUpdate(prevProps) {
    const {location: prevLocation} = prevProps;
    const {location} = this.props;

    const {search: prevSearch} = prevLocation;
    const {search} = location;

    const hasRouteChanged = prevSearch !== search;
    const {start = '', end = ''} = location.query;

    if (hasRouteChanged) {
      callAction(teacherPracticeExamsListActions.SET_LAST_VALID_DATES, {
        startDate: start,
        endDate: end
      });
    }
  }

  handleExamDeletion = (exam, examsQuery) => {
    exam
      .delete()
      .then(() => {
        addToast({
          type: 'success',
          title: 'Success!',
          message: 'Your assessment was successfully deleted'
        });
      })
      .catch((e) => {
        logger.error('Failed deleting assessment: %s', e.message || 'unknown error');
        addGenericErrorToast();
      });
    examsQuery.invalidateInterest();
  };

  toggleShowAssignPracticeExamModal = () => {
    this.setState((state) => ({
      showAssignPracticeExamModal: !state.showAssignPracticeExamModal
    }));
  };

  getExamLinkProps = makeLinkRowProps((exam) => {
    return exam.getSections().isEmpty()
      ? `/classes/${appStore.routerProps.params.classId}/assignments/${exam.getId()}/student-detail`
      : `${appStore.routerProps.location.pathname}/${exam.getId()}`;
  });

  trackSort = (sortVal) => {
    this.props.tracking.trackEvent({
      event: 'click',
      type: 'sort',
      content: sortVal === 'name' ? 'assessment' : sortVal
    });
  };

  render() {
    if (!process.env.IS_BROWSER) {
      return null;
    }
    const examsQuery = teacherPracticeExamsListStore.getTeacherPracticeExamsQuery();

    const {query} = this.props.location;
    const {range = RANGES.ALL_TIME, start = '', end = ''} = query;

    const chosenDate = Map({
      id: range,
      start,
      end,
      value: range
    });
    return (
      <div className='teacher-practice-exams-list__wrapper'>
        <div className='teacher-practice-exams-list__wrapper__sidebar'>
          <DatePicker.Provider
            onSubmit={(params) => {
              pushQueryParams(params);
            }}
            value={chosenDate}
          >
            <TrackedDatePickerSelectField className='u-mar-b_2' label='Date range' />
            {/* @todo use CSS rather than this div */}
            <div>
              <TrackedDatePickerStartDateField />
              <DatePicker.ArrowIcon className='u-mar-lr_1' />
              <TrackedDatePickerEndDateField />
            </div>
            <DatePicker.ErrorMsg />
            <TrackedDatePickerApplyBtn className='u-width_100pc u-mar-t_2' />
          </DatePicker.Provider>
        </div>
        <div className='teacher-practice-exams-list__main-items-wrapper'>
          <TrackedButton
            size='s'
            className='teacher-practice-exams-list__assign-exam-button'
            onClick={this.toggleShowAssignPracticeExamModal}
          >
            Assign assessment
          </TrackedButton>
          <Tabs content={this.tabs}>
            <MandarkTable
              query={examsQuery}
              storeName='TeacherPracticeExamsListTableStore'
              rowPropsFunc={this.getExamLinkProps}
              className='teacher-practice-exams-list-table'
              onSortCallback={this.trackSort}
            >
              <Column
                defaultSortParam
                heading='Due Date'
                sortBy='due_date'
                content={(exam) => renderMoment(exam.getDueDate(), 'dateTime')}
              />
              <Column heading='Assessment' sortBy='name' content={(exam) => exam.getName()} />
              <Column heading='Questions' content={(exam) => exam.getCountOfQuestions()} />
              <Column
                heading='Progress'
                content={(exam) => {
                  // @todo: This is not the right component to be using for this
                  return (
                    <AssignmentCompletionStatus
                      tiny
                      numberSubmitted={exam.getMeta().getCountOfCompletedStudents()}
                      numberSubmittedLate={0}
                      numberNotSubmitted={exam.getMeta().getCountOfIncompleteStudents()}
                    />
                  );
                }}
              />
              <Column
                heading='Options'
                content={(exam) => (
                  <TeacherPracticeExamOptionsDropdown
                    onEditRoute={`/create-assignment/assignment/${exam.getId()}/classroom/${
                      appStore.routerProps.params.classId
                    }/edit`}
                    exam={exam}
                    onDelete={() => this.handleExamDeletion(exam, examsQuery)}
                    data-testid='teacher-practice-exams-list-dropdown__edit-btn'
                  />
                )}
              />
            </MandarkTable>
          </Tabs>
          {this.state.showAssignPracticeExamModal ? (
            <AssignPracticeExamModal onModalClose={this.toggleShowAssignPracticeExamModal} />
          ) : null}
        </div>
      </div>
    );
  }
}

export default TeacherPracticeExamsList;
